import React, {useEffect, useState} from 'react';
import IntlMessage, {IntlMessageText} from "../../../../../components/IntlMessage/IntlMessage";
import {Button, Card, Checkbox, Divider, Flex, Form, Input, message, Modal, Radio, Select, Table} from "antd";
import classes from "../../Applications/application.module.css";
import CertificateService from "../../../../../services/CertificateService";
import MyText from "../../../../../components/UI/Text/MyText";
import {Document, Page} from "react-pdf";
import {API_BASE_URL} from "../../../../../constants/api";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import {useDispatch, useSelector} from "react-redux";
import {getAllCertificates} from "../../../../../store/slices/CertificateSlice";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import debounce from "lodash/debounce";
import {ExclamationCircleFilled, PlusOutlined} from "@ant-design/icons";
import {LocalName} from "../../../../../utils/LocalName";

const CertificateList = () => {
    const dispatch = useDispatch();
    const {certificates, isLoading, hasMore, error} = useSelector(state => state.certificateList)
    const categoriesList = useSelector(state => state.categoriesList);
    // Удаление сертификата доступно только админу (директор/ректор тоже видят этот список).
    const {user} = useSelector(state => state.user);
    const isAdmin = user?.role?.roleName === 'admin';

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    // п.6: фильтр по шаблону, статус подписи, сортировка
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [signedFilter, setSignedFilter] = useState(null); // null=все, true=подписанные, false=неподписанные
    const [sortBy, setSortBy] = useState(null);   // 'code' | 'date'
    const [sortDir, setSortDir] = useState(null);  // 'asc' | 'desc'
    const [templates, setTemplates] = useState([]);
    const [downloadingAll, setDownloadingAll] = useState(false);

    const [initialLoading, setInitialLoading] = useState(true);


    const [viewCertificate, setViewCertificate] = useState(false)
    const [openViewCertificate, setOpenViewCertificate] = useState(false)
    const [certificateNumPages, setCertificateNumPages] = useState();
    const [certificatePageNumber, setCertificatePageNumber] = useState(1);

    const [searchForm] = Form.useForm();
    const [searchParams, setSearchParams] = useState({ username: '', issueDate: '' });

    // Удаление сертификата (только админ): цель + подтверждение для подписанных ЭЦП + индикатор
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSignedConfirmed, setDeleteSignedConfirmed] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchCertificates = () => {
        dispatch(getAllCertificates({
            page: currentPage,
            limit: pageSize,
            categoryId: selectedCategoryId,
            templateId: selectedTemplateId,
            signed: signedFilter,
            sortBy,
            sortDir,
            ...searchParams,
        }));
    };

    useEffect(() => {
        const debouncedFetch = debounce(fetchCertificates, 500); // 500ms debounce

        debouncedFetch();

        return () => {
            debouncedFetch.cancel();
        };
    }, [searchParams, currentPage, pageSize, selectedCategoryId, selectedTemplateId, signedFilter, sortBy, sortDir]);

    useEffect(() => {
        setCurrentPage(1);
    }, [pageSize]);

    // п.6: подгружаем список шаблонов для фильтра
    useEffect(() => {
        CertificateService.getAllCertificateTemplates(1, 1000)
            .then((r) => setTemplates(r.data || []))
            .catch(() => setTemplates([]));
    }, []);

    // п.6: серверная сортировка по клику на заголовок колонки (пагинацию не трогаем)
    const handleTableChange = (pagination, filters, sorter, extra) => {
        if (extra?.action !== 'sort') {
            return;
        }
        const order = sorter?.order; // 'ascend' | 'descend' | undefined
        if (!order) {
            setSortBy(null);
            setSortDir(null);
        } else {
            setSortBy(sorter.columnKey);
            setSortDir(order === 'ascend' ? 'asc' : 'desc');
        }
        setCurrentPage(1);
    };

    // п.2: скачать все сертификаты под текущим фильтром одним ZIP
    async function downloadAll() {
        setDownloadingAll(true);
        message.loading(<IntlMessage id={'loading'}/>, 0);
        try {
            const r = await CertificateService.downloadAllCertificates(
                searchParams.username, searchParams.issueDate, signedFilter, selectedCategoryId, selectedTemplateId
            );
            triggerDownload(r.data, "certificates.zip");
            message.destroy();
            message.success(<IntlMessage id={'success'}/>, 5);
        } catch (e) {
            message.destroy();
            message.error(<IntlMessage id={'error'}/>, 5);
        } finally {
            setDownloadingAll(false);
        }
    }

    // Удаление сертификата: открываем модалку-предупреждение, а не удаляем по клику.
    function openDeleteModal(record) {
        setDeleteTarget(record);
        setDeleteSignedConfirmed(false);
    }

    function closeDeleteModal() {
        if (deleting) {
            return;
        }
        setDeleteTarget(null);
        setDeleteSignedConfirmed(false);
    }

    async function confirmDelete() {
        if (!deleteTarget || deleting) {
            return;
        }
        const isSigned = !!deleteTarget.signature;
        if (isSigned && !deleteSignedConfirmed) {
            return;
        }
        setDeleting(true);
        try {
            await CertificateService.deleteCertificate(deleteTarget.id, isSigned);
            message.success(<IntlMessage id={'certificate.deleted'}/>, 5);
            setDeleteTarget(null);
            setDeleteSignedConfirmed(false);
            // если удалили последнюю запись на странице — отходим на страницу назад,
            // иначе просто перезапрашиваем текущую
            if (certificates?.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                fetchCertificates();
            }
        } catch (e) {
            const status = e?.response?.status;
            message.error(
                status === 403
                    ? <IntlMessage id={'certificate.deleteForbidden'}/>
                    : <IntlMessage id={'error'}/>,
                5
            );
        } finally {
            setDeleting(false);
        }
    }

    const columns = [
        {
            title: <IntlMessage id={'certificate.code'}/>,
            key: 'code',
            sorter: true,
            sortOrder: sortBy === 'code' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
            render: (_, record) => (
                <p>{record.code}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'group'} />,
            render: (_, record) => (
                <p>{record?.groupName !== null ? record.groupName : ""}</p>
            ),
            width: '20%',
        },
        {
            title: <IntlMessage id={'certificate.user'}/>,
            render: (_, record) => (
                <p>{record.user !== null ? (!!record?.user?.profile ? record.user.profile.fullName : record.user.username) : ""}</p>
            ),
            width: '20%',
        },
        {
            title: <IntlMessage id={'certificate.issueDate'}/>,
            key: 'date',
            sorter: true,
            sortOrder: sortBy === 'date' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
            render: (_, record) => (
                <p>{new Date(record.date).toLocaleDateString()}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'certificate.expirationDate'}/>,
            render: (_, record) => (
                <p>{new Date(record.expirationDate).toLocaleDateString()}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'rectorSignature'}/>,
            render: (_, record) => (
                <p>{record.signature !== null ? <PlusOutlined /> : ""}</p>
            ),
            width: '5%',
        },
        {
            title: <IntlMessage id={'actions'}/>,
            render: (_, record) => (
                <Flex style={{width: "100%"}} align={"center"} justify={"center"} wrap={"wrap"} gap={20}>
                    {
                        record?.certificateTemplate ? (
                            <>
                                <p onClick={() => {
                                    setViewCertificate(record);
                                    setOpenViewCertificate(true);
                                }} className={classes.copyLinkBtn}><IntlMessage id={'view'}/></p>
                                <p onClick={() => downloadDocument(record?.id, record)}
                                   className={classes.copyLinkBtn}><IntlMessage id={'download'}/></p>
                            </>
                        ) : (
                            // п.5: черновик/внешний — PDF нет
                            <span style={{color: "#999"}}>
                                {record?.certificateType === 'EXTERNAL'
                                    ? <IntlMessage id={'certificate.type.external'}/>
                                    : <IntlMessage id={'certificate.type.draft'}/>}
                            </span>
                        )
                    }
                    {
                        // удалять сертификаты может только админ
                        isAdmin && (
                            <p onClick={() => openDeleteModal(record)}
                               className={classes.copyLinkBtn}
                               style={{color: "#cf1322"}}><IntlMessage id={'delete'}/></p>
                        )
                    }
                </Flex>
            ),
            width: '10%',
        },
    ];

    const handlePaginationChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    }

    function onDocumentLoadSuccess({numPages}: { numPages: number }): void {
        setCertificateNumPages(numPages);
    }

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename); // Set the download attribute with a filename
        document.body.appendChild(link);
        link.click();

        // Clean up and revoke the URL after download
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    async function downloadDocument(documentId, certificate) {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        // await CertificateService.getTestResourceById()
        await CertificateService.getResourceById(certificate?.code)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);

                triggerDownload(r.data, certificate?.certificateTemplate?.name + " Certificate.pdf")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    }

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
            <Card style={{width: "100%", borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"}>
                    {/*<Divider orientation="left" style={{fontWeight: 600}}>*/}
                    {/*    <IntlMessage id={'filter'}/>*/}
                    {/*</Divider>*/}
                    <Radio.Group
                        value={selectedCategoryId}
                        onChange={(e) => {
                            setSelectedCategoryId(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <Radio value={null}>
                            <IntlMessage id={'allCategories'}/>
                        </Radio>
                        {
                            categoriesList?.categories?.map((category) => (
                                <Radio value={category.id} key={category.id}>
                                    {LocalName.getName(category)}
                                </Radio>
                            ))
                        }
                    </Radio.Group>

                    {/* п.6: фильтр по шаблону, статус подписи; п.2: скачать все */}
                    <Flex style={{width: "100%", marginTop: 12}} align={"center"} justify={"flex-start"} gap={16} wrap={"wrap"}>
                        <Select
                            allowClear
                            style={{minWidth: 240}}
                            placeholder={IntlMessageText.getText({id: "certificate.filterByTemplate"})}
                            value={selectedTemplateId}
                            onChange={(val) => {
                                setSelectedTemplateId(val ?? null);
                                setCurrentPage(1);
                            }}
                            options={templates?.map((t) => ({value: t.id, label: t.name + (t.codePrefix ? ` (${t.codePrefix})` : "")}))}
                        />
                        <Radio.Group
                            value={signedFilter}
                            onChange={(e) => {
                                setSignedFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            optionType="button"
                        >
                            <Radio value={null}><IntlMessage id={'certificate.signedFilter.all'}/></Radio>
                            <Radio value={true}><IntlMessage id={'certificate.signedFilter.signed'}/></Radio>
                            <Radio value={false}><IntlMessage id={'certificate.signedFilter.unsigned'}/></Radio>
                        </Radio.Group>
                        <GreyButton loading={downloadingAll} onClick={downloadAll}>
                            <IntlMessage id={'certificate.downloadAll'}/>
                        </GreyButton>
                    </Flex>
                </Flex>
            </Card>
            <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"}>
                <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
                    <Form.Item name="username">
                        <Input
                            placeholder={IntlMessageText.getText({id: "name"})}
                            onChange={e => {
                                setSearchParams(prev => ({ ...prev, username: e.target.value }));
                                setCurrentPage(1);
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="issueDate">
                        <Input
                            type="date"
                            onChange={e => {
                                setSearchParams(prev => ({ ...prev, issueDate: e.target.value }));
                                setCurrentPage(1);
                            }}
                        />
                    </Form.Item>
                </Form>
            </Flex>
            <BlockLoading isLoading={isLoading}/>
            <Table
                bordered
                columns={columns}
                dataSource={certificates}
                onChange={handleTableChange}
                pagination={{
                    current: currentPage,
                    total: hasMore
                        ? currentPage * pageSize + 1
                        : currentPage * pageSize,
                    defaultPageSize: pageSize,
                    showSizeChanger: true,
                    pageSize: pageSize,
                    pageSizeOptions: ['5', '10', '20', '30', '50', '100'],
                    onChange: handlePaginationChange,
                    // onShowSizeChange: handlePaginationChange,
                }}
                rowKey={record => record.id}
                loading={isLoading}
                style={{
                    width: "100%",
                }}
                scroll={{x: 800}}
            />
            <Modal
                width={800}
                maskClosable={true}
                // title={<IntlMessage id={'contractsPage.details'}/>}
                open={openViewCertificate}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
                style={{
                    top: 20,
                }}
                onOk={() => {
                    setOpenViewCertificate(false)
                }}
                onCancel={() => {
                    setOpenViewCertificate(false)
                }}
                footer={[
                    <Button key="submit" type="primary" onClick={() => {
                        setOpenViewCertificate(false)
                    }}>
                        OK
                    </Button>
                ]}
            >
                {
                    viewCertificate &&
                    <Flex gap={50}
                          style={{
                              width: "100%",
                              minHeight: "calc(100vh - 300px)",
                              position: "relative",
                              padding: "0px 20px 20px 0px",
                          }}
                          vertical
                          align={"flex-start"}
                          justify={"flex-start"}>
                        <Flex align={"center"} justify={"center"} vertical
                              style={{overflowX: "auto", overflowY: "hidden", width: "100%"}}>
                            <Flex style={{
                                zIndex: 1,
                                padding: "10px 20px",
                                backgroundColor: "white",
                                borderRadius: 10,
                                boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
                            }} gap={15} justify={"space-between"} align={"center"}>
                                <Button disabled={certificatePageNumber === 1} onClick={() => {
                                    setCertificatePageNumber(certificatePageNumber - 1)
                                }}>
                                    {'<'}
                                </Button>
                                <MyText size={"small"}>
                                    {certificatePageNumber} / {certificateNumPages}
                                </MyText>
                                <Button disabled={certificatePageNumber === certificateNumPages} onClick={() => {
                                    setCertificatePageNumber(certificatePageNumber + 1)
                                }}>
                                    {'>'}
                                </Button>
                            </Flex>
                            <Document file={API_BASE_URL + "/api/certificate/resource/code/" + viewCertificate?.code}
                                      onLoadSuccess={onDocumentLoadSuccess}
                                      loading={<IntlMessage id={'documentIsLoading'}/>}
                            >
                                <Page pageNumber={certificatePageNumber} width={700}/>
                            </Document>
                        </Flex>
                        <Flex align={"flex-start"} justify={"center"} gap={20} style={{width: "100%"}}>
                            <GreyButton onClick={() => downloadDocument(viewCertificate?.code, viewCertificate)}>
                                <IntlMessage id={'download'}/>
                            </GreyButton>
                        </Flex>
                    </Flex>
                }
            </Modal>

            {/* Удаление сертификата: модалка-предупреждение, чтобы нельзя было снести запись случайным кликом */}
            <Modal
                width={560}
                open={!!deleteTarget}
                maskClosable={false}
                closable={!deleting}
                title={
                    <Flex align={"center"} gap={10}>
                        <ExclamationCircleFilled style={{color: "#cf1322", fontSize: 20}}/>
                        <span><IntlMessage id={'certificate.deleteConfirmTitle'}/></span>
                    </Flex>
                }
                onCancel={closeDeleteModal}
                footer={[
                    <Button key="cancel" onClick={closeDeleteModal} disabled={deleting}>
                        <IntlMessage id={'cancel'}/>
                    </Button>,
                    <Button
                        key="delete"
                        type="primary"
                        danger
                        loading={deleting}
                        disabled={!!deleteTarget?.signature && !deleteSignedConfirmed}
                        onClick={confirmDelete}
                    >
                        <IntlMessage id={'delete'}/>
                    </Button>
                ]}
            >
                {
                    deleteTarget &&
                    <Flex vertical gap={12} style={{width: "100%"}}>
                        <div style={{
                            background: "#fafafa",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: "10px 14px"
                        }}>
                            <p style={{margin: 0}}>
                                <b><IntlMessage id={'certificate.code'}/>:</b> {deleteTarget.code || "—"}
                            </p>
                            <p style={{margin: 0}}>
                                <b><IntlMessage id={'certificate.user'}/>:</b>{" "}
                                {deleteTarget.user
                                    ? (deleteTarget.user.profile?.fullName || deleteTarget.user.username)
                                    : "—"}
                            </p>
                            <p style={{margin: 0}}>
                                <b><IntlMessage id={'group'}/>:</b> {deleteTarget.groupName || "—"}
                            </p>
                            <p style={{margin: 0}}>
                                <b><IntlMessage id={'certificate.issueDate'}/>:</b>{" "}
                                {deleteTarget.date ? new Date(deleteTarget.date).toLocaleDateString() : "—"}
                            </p>
                        </div>

                        <p style={{margin: 0, color: "#cf1322"}}>
                            <IntlMessage id={'certificate.deleteWarning'}/>
                        </p>

                        {
                            !!deleteTarget.signature &&
                            <Flex vertical gap={8} style={{
                                background: "#fff1f0",
                                border: "1px solid #ffa39e",
                                borderRadius: 8,
                                padding: "10px 14px"
                            }}>
                                <p style={{margin: 0, fontWeight: 600, color: "#cf1322"}}>
                                    <IntlMessage id={'certificate.deleteSignedWarning'}/>
                                </p>
                                <Checkbox
                                    checked={deleteSignedConfirmed}
                                    onChange={(e) => setDeleteSignedConfirmed(e.target.checked)}
                                >
                                    <IntlMessage id={'certificate.deleteSignedConfirm'}/>
                                </Checkbox>
                            </Flex>
                        }
                    </Flex>
                }
            </Modal>
        </Flex>
    );
};

export default CertificateList;